import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ModalProvider, useModal } from "@/contexts/ModalContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import { PermissionGuard } from "@/components/guards/PermissionGuard";
import { OfflineBanner } from "@/components/common/OfflineBanner";
import { Permission } from "@/types";
import { App as CapacitorApp } from "@capacitor/app";
import { Haptics, ImpactStyle } from "@capacitor/haptics";
import { useEffect, useRef, useState } from "react";
import { parseDeepLink } from "@/lib/askbotDeeplinks";
import { confirmOtaUpdate } from "@/services/otaUpdateService";
import { toast } from "sonner";
import {
  isRootRoute,
  EXIT_CONFIRMATION_TIMEOUT,
  EXIT_CONFIRMATION_MESSAGE,
} from "@/config/navigation";
import Login from "./pages/Login";
import Index from "./pages/Index";
import Leads from "./pages/Leads";
import LeadDetail from "./pages/LeadDetail";
import Products from "./pages/Products";
import Tools from "./pages/Tools";
import Tasks from "./pages/Tasks";
import NotFound from "./pages/NotFound";
import ChannelPartnersPage from "./pages/tools/ChannelPartnersPage";
import DsaPartnersPage from "./pages/tools/DsaPartnersPage";
import ChannelPartnerDetailPage from "./pages/tools/ChannelPartnerDetailPage";
import DsaPartnerDetailPage from "./pages/tools/DsaPartnerDetailPage";
import EMICalculatorPage from "./pages/tools/EMICalculatorPage";
import BuildInfoPage from "./pages/tools/BuildInfoPage";
import CibilCheckPage from "./pages/tools/CibilCheckPage";
import SMContactPage from "./pages/tools/SMContactPage";
import SMDetailPage from "./pages/tools/SMDetailPage";
import TrainingPage from "./pages/tools/TrainingPage";
import BroadcastPage from "./pages/tools/BroadcastPage";
import BackendPage from "./pages/tools/BackendPage";
import AdminPage from "./pages/tools/AdminPage";
import NetworkMapPage from "./pages/tools/NetworkMapPage";
import PayoutsPage from "./pages/tools/PayoutsPage";
import KnowledgeSharingPage from "./pages/tools/KnowledgeSharingPage";
import { AskPage } from "./pages/tools/AskPage";
import TermsOfService from "./pages/TermsOfService";
import PrivacyPolicy from "./pages/PrivacyPolicy";

const queryClient = new QueryClient();

function AppRoutes() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { hasOpenModals, closeTopModal } = useModal();

  // Use refs to track current navigation state for back button handler
  const navigateRef = useRef(navigate);
  const locationRef = useRef(location);

  // Keep refs in sync with current values
  useEffect(() => {
    navigateRef.current = navigate;
    locationRef.current = location;
  }, [navigate, location]);

  // Handle notification taps (custom event from main.tsx)
  useEffect(() => {
    const handleNotificationTap = (event: any) => {
      const { taskId } = event.detail || {};

      if (taskId) {
        // Navigate to tasks page with the specific task
        // The tasks page can handle opening the task detail
        navigate("/tasks", { state: { openTaskId: taskId } });
      }
    };

    window.addEventListener("notificationTap", handleNotificationTap);

    return () => {
      window.removeEventListener("notificationTap", handleNotificationTap);
    };
  }, [navigate]);

  // Handle deep links from WhatsApp answers (see docs/deep-links.md).
  //
  // The route is held rather than navigated to immediately: a link tapped by a
  // signed-out user would otherwise be swallowed by the auth redirect below.
  // The second effect replays it once there is a user.
  const [pendingDeepLink, setPendingDeepLink] = useState<string | null>(null);

  useEffect(() => {
    let listener: { remove: () => void } | null = null;
    let cancelled = false;

    const accept = (url: string | null | undefined) => {
      if (!url) return;
      const route = parseDeepLink(url);
      if (!route) {
        console.warn("Ignoring deep link with no usable route:", url);
        return;
      }
      setPendingDeepLink(route);
    };

    const setup = async () => {
      try {
        const registered = await CapacitorApp.addListener("appUrlOpen", ({ url }) => accept(url));
        if (cancelled) registered.remove();
        else listener = registered;

        // A cold start opens the app *because of* the URL, and that event can
        // fire before the listener above exists. getLaunchUrl() is the only way
        // to see it.
        const launch = await CapacitorApp.getLaunchUrl();
        if (!cancelled) accept(launch?.url);
      } catch (e) {
        // Web build — no native App plugin.
        console.log("Deep link listener not available:", e);
      }
    };

    setup();

    return () => {
      cancelled = true;
      listener?.remove();
    };
  }, []);

  useEffect(() => {
    if (!pendingDeepLink || !user) return;
    setPendingDeepLink(null);
    navigate(pendingDeepLink);
  }, [pendingDeepLink, user, navigate]);

  // Handle Android back button - single persistent listener with enhanced features
  useEffect(() => {
    let backButtonListener: { remove: () => void } | null = null;
    let isCleaningUp = false;
    let lastBackPressTime = 0;

    // Register listener once
    const setupListener = async () => {
      try {
        backButtonListener = await CapacitorApp.addListener(
          "backButton",
          async ({ canGoBack }) => {
            // Use refs to access current state without recreating listener
            const currentPath = locationRef.current.pathname;
            console.log(
              "Back button pressed. Can go back:",
              canGoBack,
              "Current path:",
              currentPath,
            );

            // Provide haptic feedback on every back button press
            try {
              await Haptics.impact({ style: ImpactStyle.Light });
            } catch (e) {
              // Haptics might not be available on all devices
              console.log("Haptics not available:", e);
            }

            // Priority 1: Check if any modal/dialog is open using context
            if (hasOpenModals()) {
              console.log("Modal is open, closing top modal");
              closeTopModal();
              return;
            }

            // Priority 2: Handle root routes with double-tap-to-exit pattern
            if (isRootRoute(currentPath)) {
              const currentTime = Date.now();
              const timeSinceLastPress = currentTime - lastBackPressTime;

              if (timeSinceLastPress < EXIT_CONFIRMATION_TIMEOUT) {
                // Second press within timeout - exit app
                console.log("Double tap detected, exiting app");
                try {
                  await Haptics.impact({ style: ImpactStyle.Medium });
                } catch (e) {
                  console.log("Haptics not available:", e);
                }
                CapacitorApp.exitApp();
              } else {
                // First press - show toast and update timestamp
                console.log(
                  "First back press on root route, showing exit confirmation",
                );
                lastBackPressTime = currentTime;
                toast(EXIT_CONFIRMATION_MESSAGE, {
                  duration: EXIT_CONFIRMATION_TIMEOUT,
                });
              }
            }
            // Priority 3: Navigate back in history
            else if (canGoBack) {
              console.log("Navigating back");
              navigateRef.current(-1);
            }
            // Priority 4: Exit if can't go back
            else {
              console.log("Cannot go back, exiting app");
              CapacitorApp.exitApp();
            }
          },
        );

        if (!isCleaningUp) {
          console.log("Back button listener registered with enhanced features");
        }
      } catch (error) {
        console.error("Failed to register back button listener:", error);
      }
    };

    setupListener();

    return () => {
      isCleaningUp = true;
      if (backButtonListener) {
        backButtonListener.remove();
        console.log("Back button listener removed");
      }
    };
  }, [hasOpenModals, closeTopModal]); // Include modal context dependencies

  // Show loading screen while checking auth state
  if (loading) {
    return (
      <div className="min-h-screen min-h-dvh flex flex-col items-center justify-center bg-gradient-to-br from-slate-50 via-slate-50/95 to-teal-50/30 relative overflow-hidden">
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div
            className="absolute -top-40 -right-40 w-[600px] h-[600px] bg-gradient-to-br from-teal-400/15 to-cyan-400/15 rounded-full blur-3xl animate-pulse"
            style={{ animationDuration: "4s" }}
          />
          <div
            className="absolute -bottom-40 -left-40 w-[700px] h-[700px] bg-gradient-to-tr from-blue-900/8 to-indigo-900/8 rounded-full blur-3xl animate-pulse"
            style={{ animationDuration: "6s" }}
          />
        </div>

        <div className="text-center space-y-6 relative z-10 animate-in fade-in duration-700">
          {/* Logo */}
          <div className="inline-flex items-center justify-center w-[88px] h-[88px] rounded-[24px] bg-gradient-to-br from-[#2d5a6b] via-[#3a7680] to-[#4ea39f] shadow-2xl shadow-teal-500/30 mb-3 relative group">
            <div
              className="absolute inset-0 rounded-[24px] bg-gradient-to-br from-teal-400/40 to-cyan-400/40 blur-xl opacity-60 animate-pulse"
              style={{ animationDuration: "3s" }}
            />
            <span className="text-4xl font-bold text-white relative z-10 drop-shadow-lg">
              D
            </span>
          </div>

          {/* Brand Name */}
          <div className="space-y-2">
            <h1 className="text-[42px] font-extrabold bg-gradient-to-r from-[#2d5a6b] via-[#3a7680] to-[#4ea39f] bg-clip-text text-transparent tracking-tight leading-none">
              DsaLens
            </h1>
            <p className="text-[14px] text-slate-500 font-medium">DSA Portal</p>
          </div>

          {/* Loading Spinner */}
          <div className="flex flex-col items-center gap-4 pt-4">
            <div className="relative">
              <div className="w-12 h-12 rounded-full border-4 border-slate-200"></div>
              <div className="absolute inset-0 w-12 h-12 rounded-full border-4 border-transparent border-t-teal-500 border-r-teal-500 animate-spin"></div>
            </div>
            <p className="text-[14px] text-slate-500 font-medium">
              Loading your workspace...
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      <Route
        path="/"
        element={user ? <Navigate to="/dashboard" replace /> : <Login />}
      />
      <Route path="/terms-of-service" element={<TermsOfService />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Index />
          </ProtectedRoute>
        }
      />
      <Route
        path="/leads"
        element={
          <ProtectedRoute>
            <Leads />
          </ProtectedRoute>
        }
      />
      <Route
        path="/leads/:id"
        element={
          <ProtectedRoute>
            <LeadDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/products"
        element={
          <ProtectedRoute>
            <Products />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tools"
        element={
          <ProtectedRoute>
            <Tools />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tools/channel-partners"
        element={
          <ProtectedRoute>
            <ChannelPartnersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tools/channel-partners/:id"
        element={
          <ProtectedRoute>
            <ChannelPartnerDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tools/dsa-partners"
        element={
          <ProtectedRoute>
            <DsaPartnersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tools/dsa-partners/:id"
        element={
          <ProtectedRoute>
            <DsaPartnerDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tools/emi-calculator"
        element={
          <ProtectedRoute>
            <EMICalculatorPage />
          </ProtectedRoute>
        }
      />
      {import.meta.env.VITE_ENVIRONMENT !== "production" && (
        <Route
          path="/tools/build-info"
          element={
            <ProtectedRoute>
              <BuildInfoPage />
            </ProtectedRoute>
          }
        />
      )}
      <Route
        path="/tools/cibil-check"
        element={
          <ProtectedRoute>
            <CibilCheckPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tools/sm-contact"
        element={
          <ProtectedRoute>
            <SMContactPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tools/sm-contact/:id"
        element={
          <ProtectedRoute>
            <SMDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tools/training"
        element={
          <ProtectedRoute>
            <TrainingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tools/broadcast"
        element={
          <ProtectedRoute>
            <PermissionGuard requiredPermission={Permission.SHOW_IN_BROADCAST}>
              <BroadcastPage />
            </PermissionGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/tools/backend"
        element={
          <ProtectedRoute>
            <PermissionGuard
              requiredPermission={Permission.ACCESS_BACKEND_TOOLS}
            >
              <BackendPage />
            </PermissionGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/tools/admin"
        element={
          <ProtectedRoute>
            <PermissionGuard requiredPermission={Permission.ACCESS_ADMIN_TOOLS}>
              <AdminPage />
            </PermissionGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/tools/network-map"
        element={
          <ProtectedRoute>
            <PermissionGuard requiredPermission={Permission.ACCESS_ADMIN_TOOLS}>
              <NetworkMapPage />
            </PermissionGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/tools/payouts"
        element={
          <ProtectedRoute>
            <PayoutsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tools/knowledge-sharing"
        element={
          <ProtectedRoute>
            <KnowledgeSharingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tools/askbot"
        element={
          <ProtectedRoute>
            <AskPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tasks"
        element={
          <ProtectedRoute>
            <Tasks />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

const App = () => {
  // Signal that the current OTA bundle loaded successfully.
  // This prevents auto-rollback after a live update.
  useEffect(() => {
    void confirmOtaUpdate();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Sonner position="top-center" richColors />
        <BrowserRouter
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <AuthProvider>
            <ModalProvider>
              <OfflineBanner />
              <AppRoutes />
            </ModalProvider>
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
